import { useTranslation } from 'next-i18next';
import { FC, ComponentProps, useEffect, useState } from 'react';

import CallToActionContainer from '@/components/common/callToAction/CallToActionContainer';
import MillesimaPrice from '@/components/common/price/MillesimaPrice';
import MillesimaLink from '@/components/common/MillesimaLink';

import { useAppDispatch } from '@/redux';
import { addCartProduct } from '@/redux/slices/cart.slice';
import { addToast } from '@/redux/slices/toast.slice';
import { useServices } from '@/services/useServices';
import { convertToWebPUrl } from '@/utils/productUtils';

import styles from './ProductSlide.module.scss';

const CONTAINER_RATIO = 1;
const IMAGE_RATIO = 3 / 4;
const PRODUCT_VIEW_RATIO = 4 / 3;
const defaultQuickAddQuantity = 1;
const defaultSize = 150;

type ProductSlideProps = {
  slide: {
    _id: string | number;
    seoKeyword: string;
    product: string;
    categ: string;
    image: Record<string, string>;
    item: Record<string, string>;
  };
  backgroundColor: string;
  slideSize?: number;
  cta?: boolean;
  origin: 'fromSameProducer' | 'fromAlgolia';
  position: 'productView' | 'sideModal' | 'blog';
} & Pick<ComponentProps<'div'>, 'key'>;

export const ProductSlide: FC<ProductSlideProps> = (
  { slide, backgroundColor, slideSize, cta = false, position = 'sideModal', origin = 'fromAlgolia' }
) => {
  const [size, setSize] = useState(defaultSize);
  const { datalayerService } = useServices();
  const dispatch = useAppDispatch();
  const t = useTranslation().t;

  const handleAddToCart = () => {
    dispatch(addCartProduct({ item: slide?.item.partnumber, qty: defaultQuickAddQuantity, options: {
      showConfirm: true,
      set: false
    }, error: undefined }));
    dispatch(addToast({ msg: t('toast.addedProduct'), err: false }));
  };

  const handleOnclick = ({ _id, product, seoKeyword }) => {
    datalayerService.pushProductCarouselClick({
      _id,
      name: product,
      seoKeyword: seoKeyword.replace('/', '')
    });
  };

  const computedContainerRatio = position === 'productView' ? PRODUCT_VIEW_RATIO : CONTAINER_RATIO;

  useEffect(() => setSize((previousSize) => !Number.isNaN(slideSize) ? slideSize : previousSize), [slideSize]);

  const getLabel = () => {
    if (cta) return null;
    if (origin === 'fromSameProducer') return slide?.categ;
    return slide?.item?.conditioning;
  };

  return (
    <div className={styles.container}>
      <MillesimaLink href={slide?.seoKeyword} type="no-decoration" customClassName={{ container: styles['link-container'] }}>
        <div
          key={slide?._id}
          data-test-id={`slide-${slide?._id}`}
          className={styles.container}
          data-rbf={`product-slide-${slide?._id}`}
          style={{ backgroundColor }}
          onClick={() => handleOnclick(slide)}
        >
          <div
            className={styles['img-top-container']}
            style={{ height: size * computedContainerRatio }}
          > <picture>
              <source srcSet={convertToWebPUrl(slide?.image.src)} type="image/webp"/>
              <img
                src={slide?.image.src}
                alt={slide?.image.alt}
                className={styles['img-top']}
                style={{ height: size * IMAGE_RATIO }}
                loading="lazy"
              />
            </picture>
          </div>
          {(slide?.categ || slide?.product || slide?.item) && (
            <div className={styles.body} style={{ minHeight: size / 2 }}>
              <div className={styles.title}>
                {getLabel()}
              </div>
              <div className={styles.text}>{slide?.product}</div>
            </div>
          )}
          {cta && (
            <div className={styles['price-container']}>
              <MillesimaPrice className={{ container: styles.price }} value={slide?.item?.price}/>
              <div className={styles.conditioning}>
                {slide?.item?.conditioning}
              </div>
            </div>
          )}
        </div>
      </MillesimaLink>
      {cta && (
        <CallToActionContainer kind="secondary" className={styles.button} onClick={handleAddToCart}>
          {t('btn.buy')}
        </CallToActionContainer>
      )}
    </div>
  );
};

export default ProductSlide;
